import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api/axios'
import toast from 'react-hot-toast'

const Contact = () => {
    const [settings, setSettings] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/api/settings');
                if (res.data) setSettings(res.data);
            } catch (err) {
                console.error("Failed to load contact settings", err);
            }
        };
        fetchSettings();
    }, []);

    const phone = settings?.storePhone || settings?.phone || '+91 9449 818 329';
    const email = settings?.storeEmail || settings?.email || 'healthyhomesales@gmail.com';
    const address = settings?.storeAddress || settings?.address || 'A306 Durga Petals Apartment Near rainbow children hospital Doddanekundi Bangalore 560 037';

    const validateField = (field, value) => {
        let error = '';
        if (field === 'name') {
            if (!value || !value.trim()) {
                error = 'Full name is required';
            }
        } else if (field === 'email') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!value || !value.trim()) {
                error = 'Email address is required';
            } else if (!emailRegex.test(value.trim())) {
                error = 'Please enter a valid email address (e.g. name@example.com)';
            }
        } else if (field === 'phone') {
            const digits = (value || '').replace(/\D/g, '');
            if (!digits) {
                error = 'Phone number is required';
            } else if (digits.length !== 10) {
                error = `Phone number must be exactly 10 digits (${digits.length}/10)`;
            }
        } else if (field === 'message') {
            if (!value || !value.trim()) {
                error = 'Message is required';
            } else if (value.trim().length < 10) {
                error = 'Message must be at least 10 characters long';
            }
        }
        return error;
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));

        if (touched[id]) {
            const error = validateField(id, value);
            setErrors(prev => ({ ...prev, [id]: error }));
        }
    };

    // Phone-specific handler: only digits, max 10
    const handlePhoneChange = (e) => {
        const rawValue = e.target.value;
        const digitsOnly = rawValue.replace(/\D/g, '').slice(0, 10);
        setFormData(prev => ({ ...prev, phone: digitsOnly }));

        if (touched.phone) {
            const error = validateField('phone', digitsOnly);
            setErrors(prev => ({ ...prev, phone: error }));
        }
    };

    // Block non-digit keys on keydown for phone
    const handlePhoneKeyDown = (e) => {
        if (
            ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key) ||
            ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
        ) {
            return;
        }
        if (!/^[0-9]$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        const error = validateField(field, formData[field]);
        setErrors(prev => ({ ...prev, [field]: error }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        const newErrors = {};
        ['name', 'email', 'phone', 'message'].forEach((field) => {
            const err = validateField(field, formData[field]);
            if (err) newErrors[field] = err;
        });

        setErrors(newErrors);
        setTouched({ name: true, email: true, phone: true, message: true });

        if (Object.keys(newErrors).length > 0) {
            const firstKey = Object.keys(newErrors)[0];
            toast.error(newErrors[firstKey]);
            const element = document.getElementById(firstKey);
            if (element) element.focus();
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await api.post('/api/contact', formData);
            toast.success(res.data.message || 'Message sent successfully!');
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: ''
            });
            setErrors({});
            setTouched({});
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="contact py-80">
            <div className="container container-lg">
                <div className="row gy-5">
                    <div className="col-lg-8 wow fadeInLeft" data-wow-duration="0.8s">
                        <div className="contact-box border border-gray-100 rounded-16 px-24 py-40 bg-white">
                            <form onSubmit={handleSubmit} noValidate>
                                <h6 className="mb-32">Make Custom Request</h6>
                                <div className="row gy-4">
                                    {/* Full Name */}
                                    <div className="col-12 col-sm-6">
                                        <label
                                            htmlFor="name"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Full Name <span className="text-danger text-xl line-height-1">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="common-input px-16"
                                            id="name"
                                            placeholder="Full name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('name')}
                                            style={errors.name && touched.name ? { borderColor: '#EF4444' } : {}}
                                            required
                                        />
                                        {errors.name && touched.name && (
                                            <span className="text-danger text-xs mt-4 d-flex align-items-center gap-4">
                                                <i className="ph ph-warning-circle text-sm" />
                                                {errors.name}
                                            </span>
                                        )}
                                    </div>

                                    {/* Email Address */}
                                    <div className="col-12 col-sm-6">
                                        <label
                                            htmlFor="email"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Email Address <span className="text-danger text-xl line-height-1">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            className="common-input px-16"
                                            id="email"
                                            placeholder="name@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('email')}
                                            style={errors.email && touched.email ? { borderColor: '#EF4444' } : {}}
                                            required
                                        />
                                        {errors.email && touched.email && (
                                            <span className="text-danger text-xs mt-4 d-flex align-items-center gap-4">
                                                <i className="ph ph-warning-circle text-sm" />
                                                {errors.email}
                                            </span>
                                        )}
                                    </div>

                                    {/* Phone Number (10 digits, numbers only) */}
                                    <div className="col-sm-12">
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <label
                                                htmlFor="phone"
                                                className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold m-0"
                                            >
                                                Phone Number <span className="text-danger text-xl line-height-1">*</span>
                                            </label>
                                            {formData.phone.length > 0 && (
                                                <span
                                                    className="text-xs fw-medium"
                                                    style={{
                                                        color: formData.phone.length === 10 ? '#10B981' : '#64748B',
                                                    }}
                                                >
                                                    {formData.phone.length}/10 digits
                                                </span>
                                            )}
                                        </div>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={10}
                                            pattern="[0-9]{10}"
                                            className="common-input px-16"
                                            id="phone"
                                            placeholder="Enter 10-digit mobile number"
                                            value={formData.phone}
                                            onChange={handlePhoneChange}
                                            onKeyDown={handlePhoneKeyDown}
                                            onBlur={() => handleBlur('phone')}
                                            style={errors.phone && touched.phone ? { borderColor: '#EF4444' } : {}}
                                            required
                                        />
                                        {errors.phone && touched.phone && (
                                            <span className="text-danger text-xs mt-4 d-flex align-items-center gap-4">
                                                <i className="ph ph-warning-circle text-sm" />
                                                {errors.phone}
                                            </span>
                                        )}
                                    </div>

                                    {/* Message */}
                                    <div className="col-sm-12">
                                        <label
                                            htmlFor="message"
                                            className="flex-align gap-4 text-sm font-heading-two text-gray-900 fw-semibold mb-4"
                                        >
                                            Message <span className="text-danger text-xl line-height-1">*</span>
                                        </label>
                                        <textarea
                                            className="common-input px-16"
                                            id="message"
                                            placeholder="Type your message (at least 10 characters)"
                                            value={formData.message}
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('message')}
                                            style={errors.message && touched.message ? { borderColor: '#EF4444' } : {}}
                                            required
                                            minLength={10}
                                        />
                                        {errors.message && touched.message && (
                                            <span className="text-danger text-xs mt-4 d-flex align-items-center gap-4">
                                                <i className="ph ph-warning-circle text-sm" />
                                                {errors.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="col-sm-12 mt-32">
                                        <button
                                            type="submit"
                                            className="btn btn-main py-18 px-32 rounded-8 hover-lift"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Sending...' : 'Get a Quote'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="col-lg-4 wow fadeInRight" data-wow-duration="0.8s">
                        <div className="contact-box border border-gray-100 rounded-16 px-24 py-40 bg-white hover-lift">
                            <h6 className="mb-32">Get In Touch</h6>
                            <div className="flex-align align-items-start gap-16 mb-24">
                                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 text-main-two-600 text-2xl flex-shrink-0 mt-4">
                                    <i className="ph-fill ph-map-pin" />
                                </span>
                                <div>
                                    <span className="d-block text-sm text-gray-500 mb-4 fw-medium">Business Address</span>
                                    <span className="text-md fw-semibold text-gray-900 d-block mb-4">Healthy Home</span>
                                    <span className="text-md text-gray-700 line-height-base d-block">
                                        {address}
                                    </span>
                                </div>
                            </div>
                            <div className="flex-align align-items-center gap-16 mb-24">
                                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 text-main-two-600 text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-phone-call" />
                                </span>
                                <div>
                                    <span className="d-block text-sm text-gray-500 mb-4 fw-medium">Phone Number</span>
                                    <a
                                        href={`tel:${phone.replace(/\s+/g, '')}`}
                                        className="text-md text-gray-900 hover-text-main-600 fw-medium"
                                    >
                                        {phone}
                                    </a>
                                </div>
                            </div>
                            <div className="flex-align align-items-center gap-16">
                                <span className="w-40 h-40 flex-center rounded-circle border border-gray-100 text-main-two-600 text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-envelope" />
                                </span>
                                <div>
                                    <span className="d-block text-sm text-gray-500 mb-4 fw-medium">Email Address</span>
                                    <a
                                        href={`mailto:${email}`}
                                        className="text-md text-gray-900 hover-text-main-600 fw-medium"
                                    >
                                        {email}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Contact

